import {
  CustomFieldEntityType,
  type CustomFieldDefinition,
} from 'src/api/generated';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { Badge } from 'src/modules/shared/components/atoms/badge';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { PiDotsNine, PiPencil, PiTrash } from 'react-icons/pi';
import { formatCustomFieldType } from 'src/modules/settings/utils/custom-fields.util';
import { type DraggableChildrenProps } from 'src/modules/shared/components/molecules/draggable-item';
import { resolveDateCustomFieldIncludeTime } from 'src/modules/shared/utils/custom-field-definition.util';

interface CustomFieldDefinitionCardProps {
  definition: CustomFieldDefinition;
  positionLabel?: number;
  DragHandle?: DraggableChildrenProps['DragHandle'];
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: (id: number) => void;
  onDeleteClick: (def: CustomFieldDefinition) => void;
  overlay?: boolean;
  funnelNames?: string[];
}

export const CustomFieldDefinitionCard: React.FC<
  CustomFieldDefinitionCardProps
> = ({
  definition,
  positionLabel,
  DragHandle,
  canUpdate,
  canDelete,
  onEdit,
  onDeleteClick,
  overlay = false,
  funnelNames,
}) => (
  <Card
    variant="neutral"
    appearance="outline"
    className={`relative overflow-visible ${overlay ? 'opacity-90' : ''} p-3`}
  >
    <div className="flex flex-col h-full mt-1">
      <div className="flex flex-col flex-1 min-w-0 gap-2">
        <div className="flex items-center gap-2 mb-1">
          {DragHandle && (
            <DragHandle
              className="inline-flex items-center justify-center  cursor-grab select-none p-1 rounded hover:bg-secondary-50 border border-transparent hover:border-secondary-200"
              // TODO: check when implementing i18n
              aria-label="Reordenar"
              title="Arrastra para reordenar"
            >
              <PiDotsNine className="w-4 h-4" />
            </DragHandle>
          )}
          <Text size="paragraph" weight="medium" color="primary">
            {definition.fieldLabel ?? definition.fieldKey}
          </Text>
          <Badge
            variant={
              definition.entityType === CustomFieldEntityType.CONTACT
                ? 'warning'
                : definition.entityType ===
                    CustomFieldEntityType.FUNNEL_CONVERSATION
                  ? 'info'
                  : 'success'
            }
            appearance="soft"
            size="sm"
          >
            {/* TODO: check when implementing i18n */}
            {definition.entityType === CustomFieldEntityType.CONTACT &&
              'Contacto'}
            {definition.entityType ===
              CustomFieldEntityType.FUNNEL_CONVERSATION && 'Conversación'}
            {definition.entityType === CustomFieldEntityType.PRODUCT &&
              'Producto'}
          </Badge>
          <Badge variant="neutral" size="sm">
            {formatCustomFieldType(
              definition.fieldType,
              resolveDateCustomFieldIncludeTime(
                definition.fieldType,
                definition.includeTime
              )
            )}
          </Badge>
          <div className="ml-auto">
            {positionLabel != null && (
              <Text size="paragraph" color="secondary" weight="medium">
                #{positionLabel}
              </Text>
            )}
          </div>
        </div>

        <div className="flex items-end gap-3">
          <div className="flex flex-col flex-1 gap-1.5">
            <Text size="small" color="secondary" weight="medium">
              {/* TODO: check when implementing i18n */}
              <span className="font-medium">Identificador:</span>{' '}
              {definition.fieldKey}
            </Text>

            {definition.description && (
              <Text size="small" color="secondary" weight="medium">
                {/* TODO: check when implementing i18n */}
                <span className="font-medium">Descripción:</span>{' '}
                {definition.description}
              </Text>
            )}

            {funnelNames && funnelNames.length > 0 && (
              <div className="flex items-center gap-2">
                <Text size="small" color="secondary" weight="medium">
                  {/* TODO: check when implementing i18n */}
                  En embudos:
                </Text>
                <div className="flex gap-1 flex-wrap">
                  {funnelNames.map((name) => (
                    <Badge
                      key={name}
                      variant="secondary"
                      appearance="soft"
                      size="sm"
                    >
                      {name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div
            className={`flex items-center gap-2 ${overlay ? 'opacity-60 pointer-events-none' : ''}`}
          >
            {canUpdate && (
              <Button
                variant="neutral"
                appearance="ghost"
                size="sm"
                disabled={overlay}
                onClick={() => onEdit(definition.id)}
                className="flex items-center gap-1"
              >
                <PiPencil className="w-4 h-4" />
                {/* TODO: check when implementing i18n */}
                <Text size="small">Editar</Text>
              </Button>
            )}
            {canDelete && (
              <Button
                variant="danger"
                appearance="ghost"
                size="sm"
                disabled={overlay}
                onClick={() => onDeleteClick(definition)}
                className="flex items-center gap-1"
              >
                <PiTrash className="w-4 h-4" />
                {/* TODO: check when implementing i18n */}
                <Text size="small">Eliminar</Text>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  </Card>
);

export default CustomFieldDefinitionCard;
